import React from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  TextField,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  BlockOutlined,
  CheckCircleOutline,
  ContentCopyOutlined,
  DangerousOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useAppRegistryStore } from "../../../stores/appRegistryStore";
import "./StripeProvisioningFailures.css";

const parseCssColor = (input) => {
  if (!input || typeof input !== "string") return null;
  const value = input.trim();
  if (!value) return null;

  const hex = value.startsWith("#") ? value.slice(1) : null;
  if (hex && (hex.length === 3 || hex.length === 6)) {
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return { r, g, b };
  }

  const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1]
      .split(",")
      .map((p) => p.trim())
      .slice(0, 3)
      .map((p) => Number(p));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    return { r: parts[0], g: parts[1], b: parts[2] };
  }

  return null;
};

const isDarkRgb = ({ r, g, b }) => {
  const srgb = [r, g, b].map((v) => v / 255);
  const linear = srgb.map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  const luminance =
    0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance < 0.35;
};

const formatUtc = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().replace("T", " ").replace("Z", " UTC");
};

const formatRelative = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 10) return "Just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  const diffW = Math.floor(diffD / 7);
  if (diffW < 5) return `${diffW}w ago`;
  return date.toISOString().slice(0, 10);
};

const statusConfig = (status) => {
  switch (status) {
    case "open":
      return {
        label: "Open",
        tone: "open",
        Icon: WarningAmberOutlined,
        retryLabel: "Retry",
        confirm: false,
        disabled: false,
      };
    case "poisoned":
      return {
        label: "Poisoned",
        tone: "poisoned",
        Icon: DangerousOutlined,
        retryLabel: "Retry",
        confirm: true,
        disabled: false,
      };
    case "max_retries_exceeded":
      return {
        label: "Max retries",
        tone: "max",
        Icon: BlockOutlined,
        retryLabel: "Force retry",
        confirm: true,
        disabled: false,
      };
    case "resolved":
      return {
        label: "Resolved",
        tone: "resolved",
        Icon: CheckCircleOutline,
        retryLabel: "Retry",
        confirm: false,
        disabled: true,
      };
    default:
      return {
        label: status || "Unknown",
        tone: "unknown",
        Icon: WarningAmberOutlined,
        retryLabel: "Retry",
        confirm: false,
        disabled: false,
      };
  }
};

const badgeClassName = (status) => {
  const tone = statusConfig(status).tone;
  return `spf-badge spf-badge-${tone}`;
};

const StripeProvisioningFailures = () => {
  const history = useHistory();
  const url = process.env.REACT_APP_BASEURL;
  const { settings } = useAppRegistryStore();

  const [statusFilter, setStatusFilter] = React.useState("all");
  const [searchInput, setSearchInput] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [limit, setLimit] = React.useState(50);
  const [limitAll, setLimitAll] = React.useState(false);
  const offsetRef = React.useRef(0);
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loadingInitial, setLoadingInitial] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [listError, setListError] = React.useState("");
  const [isDarkUi, setIsDarkUi] = React.useState(false);
  const [inFlight, setInFlight] = React.useState(() => new Set());
  const inFlightRef = React.useRef(new Set());
  const didMountRef = React.useRef(false);
  const fetchFailuresRef = React.useRef(null);
  const [detailsRow, setDetailsRow] = React.useState(null);

  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    eventId: "",
    status: "",
  });

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info",
  });

  const getToken = () => window.sessionStorage.getItem("Token");

  React.useEffect(() => {
    const root = document.documentElement;
    const computedRoot = window.getComputedStyle(root);
    const bgVar = computedRoot.getPropertyValue("--bg-primary") || "";
    const bodyBg = document.body
      ? window.getComputedStyle(document.body).backgroundColor
      : "";
    const rgb =
      parseCssColor(bodyBg) ||
      parseCssColor(bgVar) ||
      parseCssColor(computedRoot.backgroundColor || "");
    const isDarkFromTheme = settings?.currentTheme === "dark";
    setIsDarkUi(isDarkFromTheme || (rgb ? isDarkRgb(rgb) : false));
  }, [settings?.selectedTheme, settings?.currentTheme]);

  React.useEffect(() => {
    const delay = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 500);
    return () => clearTimeout(delay);
  }, [searchInput]);

  const copyText = async (text, successMessage) => {
    const value = (text || "").toString();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSnackbar({
        open: true,
        message: successMessage || "Copied",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Copy failed", severity: "error" });
    }
  };

  const fetchFailures = React.useCallback(
    async ({ reset, offsetOverride }) => {
      const token = getToken();
      if (!token) {
        history.push("/");
        return;
      }

      setListError("");
      if (reset) setLoadingInitial(true);
      else setLoadingMore(true);

      try {
        const nextOffset = reset ? 0 : (offsetOverride ?? offsetRef.current);
        const res = await axios.get(
          `${url}/api/admin/stripe-provisioning-failures`,
          {
            headers: { "x-access-token": token },
            params: {
              status: statusFilter,
              q: searchTerm || undefined,
              limit: limitAll ? "all" : limit,
              offset: limitAll ? undefined : nextOffset,
            },
          },
        );

        const result = Array.isArray(res?.data?.result) ? res.data.result : [];
        const nextTotal =
          typeof res?.data?.total === "number" ? res.data.total : 0;
        setTotal(nextTotal);

        if (reset) {
          setRows(result);
          offsetRef.current = limitAll ? 0 : result.length;
        } else {
          setRows((prev) => prev.concat(result));
          offsetRef.current = limitAll ? 0 : nextOffset + result.length;
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          window.sessionStorage.clear();
          history.push("/");
          return;
        }
        setListError(
          err?.response?.data?.message ||
            "Unable to load provisioning failures.",
        );
      } finally {
        if (reset) setLoadingInitial(false);
        else setLoadingMore(false);
      }
    },
    [history, limit, limitAll, searchTerm, statusFilter, url],
  );

  React.useEffect(() => {
    fetchFailuresRef.current = fetchFailures;
  }, [fetchFailures]);

  const resetAndFetch = React.useCallback(() => {
    offsetRef.current = 0;
    fetchFailures({ reset: true, offsetOverride: 0 });
  }, [fetchFailures]);

  React.useEffect(() => {
    const token = getToken();
    const user = window.sessionStorage.getItem("User");
    if (!token || !user) {
      history.push("/");
      return;
    }
    offsetRef.current = 0;
    fetchFailuresRef.current?.({ reset: true, offsetOverride: 0 });
  }, [history]);

  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return undefined;
    }

    const handle = setTimeout(() => resetAndFetch(), 300);
    return () => clearTimeout(handle);
  }, [resetAndFetch, searchTerm, statusFilter, limit, limitAll]);

  const doRetry = async (eventId) => {
    const id = (eventId || "").toString();
    if (!id) return;
    if (inFlightRef.current.has(id)) return;

    const token = getToken();
    if (!token) {
      history.push("/");
      return;
    }

    inFlightRef.current.add(id);
    setInFlight(new Set(inFlightRef.current));
    try {
      const res = await axios.post(
        `${url}/api/admin/stripe-provisioning-failures/retry`,
        { eventId: id },
        { headers: { "x-access-token": token } },
      );

      const refreshed = res?.data?.result || null;
      if (refreshed?.eventId) {
        setRows((prev) =>
          prev.map((r) =>
            (r?.eventId || "").toString() === refreshed.eventId ? refreshed : r,
          ),
        );
        setDetailsRow((prev) =>
          prev && (prev?.eventId || "").toString() === refreshed.eventId
            ? refreshed
            : prev,
        );
      }

      const resolved = refreshed?.status === "resolved";
      setSnackbar({
        open: true,
        message: resolved
          ? "Retry succeeded"
          : "Retry completed (still failing)",
        severity: resolved ? "success" : "info",
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        window.sessionStorage.clear();
        history.push("/");
        return;
      }
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Retry request failed",
        severity: "error",
      });
    } finally {
      inFlightRef.current.delete(id);
      setInFlight(new Set(inFlightRef.current));
    }
  };

  const requestRetry = (row) => {
    const eventId = (row?.eventId || "").toString();
    if (!eventId) return;
    const status = (row?.status || "").toString();
    const cfg = statusConfig(status);
    if (cfg.disabled) return;
    if (cfg.confirm) {
      setConfirmDialog({ open: true, eventId, status });
      return;
    }
    doRetry(eventId);
  };

  const hasMore = !limitAll && rows.length < total;

  const showNoMatches =
    !loadingInitial &&
    !listError &&
    rows.length === 0 &&
    (statusFilter !== "all" || searchInput.trim());
  const showNoFailures =
    !loadingInitial &&
    !listError &&
    rows.length === 0 &&
    statusFilter === "all" &&
    !searchInput.trim();

  const dialogPaperClassName = isDarkUi
    ? "spf-dialogPaper spf-dialogPaper-dark"
    : "spf-dialogPaper";

  return (
    <div
      className="spf-page"
      style={{
        "--spf-border": isDarkUi
          ? "rgba(255,255,255,0.14)"
          : "rgba(0,0,0,0.12)",
        "--spf-border-soft": isDarkUi
          ? "rgba(255,255,255,0.10)"
          : "rgba(0,0,0,0.08)",
        "--spf-surface-2": isDarkUi
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)",
        "--spf-shadow": isDarkUi
          ? "0 10px 24px rgba(0,0,0,0.4)"
          : "0 10px 24px rgba(0,0,0,0.08)",
        "--spf-error-bg": isDarkUi
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)",
        "--spf-error-border": isDarkUi
          ? "rgba(255,255,255,0.14)"
          : "rgba(0,0,0,0.12)",
        "--spf-error-text": isDarkUi
          ? "rgba(255,255,255,0.92)"
          : "rgba(0,0,0,0.88)",
        "--spf-error-muted": isDarkUi
          ? "rgba(255,255,255,0.72)"
          : "rgba(0,0,0,0.62)",
      }}
    >
      <div className="mx-4" style={{ marginTop: "0px" }}>
        <div className="d-flex justify-content-between align-items-center py-2 flex-wrap gap-3">
          <h5 className="section-title mb-0">Stripe Provisioning Failures</h5>
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="poisoned">Poisoned</MenuItem>
              <MenuItem value="max_retries_exceeded">Max retries</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Limit"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              disabled={limitAll || loadingInitial || loadingMore}
              sx={{ width: 110 }}
              inputProps={{ min: 1, max: 1000 }}
            />
            <label className="spf-limitAll">
              <input
                type="checkbox"
                checked={limitAll}
                onChange={(e) => setLimitAll(e.target.checked)}
                disabled={loadingInitial || loadingMore}
              />
              <span>All</span>
            </label>
            {limitAll ? <div className="spf-warn">May be slow</div> : null}
          </div>
        </div>

        <div className="spf-metaBar">
          <div className="spf-count">
            Showing {rows.length} of {total}
          </div>
          <div className="spf-sort">↓ Last failure</div>
        </div>

        {listError ? (
          <div className="spf-inlineError" role="alert">
            <div className="spf-inlineErrorTitle">Failed to load</div>
            <div className="spf-inlineErrorText">{listError}</div>
            <Button
              variant="contained"
              onClick={() => fetchFailures({ reset: true })}
            >
              Retry fetch
            </Button>
          </div>
        ) : loadingInitial ? (
          <div className="loader text-center py-5">
            <CircularProgress />
          </div>
        ) : showNoMatches ? (
          <div className="text-center py-5">
            <div className="mb-3">No failures match this filter.</div>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchInput("");
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Clear filter
            </Button>
          </div>
        ) : showNoFailures ? (
          <div className="text-center py-5">
            <div className="mb-2">No provisioning failures.</div>
            <div className="text-muted">
              All Stripe provisioning jobs are currently healthy.
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive spf-tableWrap">
              <table className="table table-hover text-left spf-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Status</th>
                    <th>Email</th>
                    <th>Event ID</th>
                    <th>Attempts</th>
                    <th>Last Failure</th>
                    <th>Resolved At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const eventId = (row?.eventId || "").toString();
                    const email = row?.email ? row.email.toString() : "";
                    const status = (row?.status || "").toString();
                    const attempts = Number.isFinite(Number(row?.attempts))
                      ? Number(row.attempts)
                      : 0;
                    const lastFailureAt = row?.lastFailureAt || "";
                    const resolvedAt = row?.resolvedAt || "";
                    const cfg = statusConfig(status);
                    const retrying = inFlight.has(eventId);

                    return (
                      <tr key={eventId || index}>
                        <td>{index + 1}</td>
                        <td>
                          <span className={badgeClassName(status)}>
                            <cfg.Icon className="spf-badgeIcon" />
                            <span>{cfg.label}</span>
                          </span>
                        </td>
                        <td className="spf-emailCell">
                          {email ? (
                            <a href={`mailto:${email}`} className="text-muted">
                              {email}
                            </a>
                          ) : (
                            <span className="text-muted">No email on file</span>
                          )}
                        </td>
                        <td className="spf-eventCell" title={eventId}>
                          <div className="spf-eventValue">{eventId || "—"}</div>
                          {eventId ? (
                            <button
                              type="button"
                              className="spf-copyInline"
                              onClick={() =>
                                copyText(eventId, "Event ID copied")
                              }
                              aria-label="Copy full eventId"
                            >
                              <ContentCopyOutlined style={{ fontSize: 15 }} />
                            </button>
                          ) : null}
                        </td>
                        <td>{attempts}</td>
                        <td>
                          <Tooltip title={formatUtc(lastFailureAt)} arrow>
                            <span>
                              {lastFailureAt
                                ? formatRelative(lastFailureAt)
                                : "—"}
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          {resolvedAt ? (
                            <Tooltip title={formatUtc(resolvedAt)} arrow>
                              <span>{formatRelative(resolvedAt)}</span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div className="spf-rowActions">
                            <button
                              type="button"
                              onClick={() => setDetailsRow(row)}
                              className="btn btn-sm d-flex align-items-center no-focus-outline spf-actionBtn"
                            >
                              Details
                            </button>
                            <button
                              type="button"
                              onClick={() => requestRetry(row)}
                              disabled={cfg.disabled || retrying || !eventId}
                              className="btn btn-sm d-flex align-items-center no-focus-outline spf-actionBtn"
                              aria-label={`${cfg.retryLabel} ${eventId}`}
                            >
                              {retrying ? (
                                <CircularProgress size={14} className="mr-1" />
                              ) : null}
                              {cfg.retryLabel}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore ? (
              <div className="spf-loadMore">
                <Button
                  variant="outlined"
                  onClick={() => fetchFailures({ reset: false })}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <span className="spf-loadingMore">
                      <CircularProgress size={18} />
                      <span>Loading</span>
                    </span>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      <Dialog
        open={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: dialogPaperClassName }}
      >
        <DialogTitle>Provisioning Details</DialogTitle>
        <DialogContent dividers>
          {detailsRow ? (
            <div className="spf-detailsGrid">
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">Status</div>
                <div>
                  <span className={badgeClassName(detailsRow.status)}>
                    {React.createElement(statusConfig(detailsRow.status).Icon, {
                      className: "spf-badgeIcon",
                    })}
                    <span>{statusConfig(detailsRow.status).label}</span>
                  </span>
                </div>
              </div>
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">Email</div>
                <div>{detailsRow.email || "No email on file"}</div>
              </div>
              <div className="spf-detailsItem spf-detailsWide">
                <div className="spf-detailsLabel">Event ID</div>
                <div className="spf-detailsMonoRow">
                  <div className="spf-detailsMono">
                    {detailsRow.eventId || "—"}
                  </div>
                  <button
                    type="button"
                    className="spf-copyInline"
                    onClick={() =>
                      copyText(detailsRow.eventId, "Event ID copied")
                    }
                    aria-label="Copy full eventId"
                    disabled={!detailsRow.eventId}
                  >
                    <ContentCopyOutlined style={{ fontSize: 15 }} />
                  </button>
                </div>
              </div>
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">Attempts</div>
                <div>{detailsRow.attempts ?? 0}</div>
              </div>
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">First Failure</div>
                <div>{formatUtc(detailsRow.firstFailureAt)}</div>
              </div>
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">Last Failure</div>
                <div>{formatUtc(detailsRow.lastFailureAt)}</div>
              </div>
              <div className="spf-detailsItem">
                <div className="spf-detailsLabel">Resolved At</div>
                <div>
                  {detailsRow.resolvedAt
                    ? formatUtc(detailsRow.resolvedAt)
                    : "—"}
                </div>
              </div>
              <div className="spf-detailsItem spf-detailsWide">
                <div className="spf-errorTop">
                  <div className="spf-detailsLabel mb-0">Last Error</div>
                  <button
                    type="button"
                    className="spf-errorCopy"
                    onClick={() =>
                      copyText(detailsRow.lastError, "Error copied")
                    }
                    aria-label="Copy full error"
                    disabled={!detailsRow.lastError}
                  >
                    Copy all
                  </button>
                </div>
                <pre className="spf-errorPre spf-errorPreDialog">
                  {detailsRow.lastError || "No error message available."}
                </pre>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsRow(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const row = detailsRow;
              if (row) requestRetry(row);
            }}
            disabled={
              !detailsRow ||
              statusConfig(detailsRow.status).disabled ||
              inFlight.has((detailsRow.eventId || "").toString()) ||
              !detailsRow.eventId
            }
          >
            {detailsRow ? statusConfig(detailsRow.status).retryLabel : "Retry"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, eventId: "", status: "" })
        }
        PaperProps={{ className: dialogPaperClassName }}
      >
        <DialogTitle>Confirm retry?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.status === "max_retries_exceeded"
              ? "This job reached the retry ceiling. Forcing a retry may repeat side effects."
              : "This job is in a poisoned state. Retrying may repeat side effects."}
            <div className="spf-confirmEvent">{confirmDialog.eventId}</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, eventId: "", status: "" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const id = confirmDialog.eventId;
              setConfirmDialog({ open: false, eventId: "", status: "" });
              if (id) doRetry(id);
            }}
          >
            {confirmDialog.status === "max_retries_exceeded"
              ? "Force retry"
              : "Retry"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StripeProvisioningFailures;
